/**
 * Integrações › **Mapas e rotas** (W8-GEO-REAL, D436, ADR-0068; owner answer D435 §5.0 items 1-2). One form per geo
 * vendor slot of the credential vault — the paid vendors' keys (write-only: a masked hint + a configured badge, never
 * the key) and the self-hosted services' addresses — each with its on/off switch and the cost of one call with its
 * currency; the self-hosted router probe; and the usage + cost of the last 30 days. The vendor ORDER is the governed
 * "Maps cascade" rule, not this panel. Composes `@vello/web-theme` only; the logic is `src/geo-integrations.ts`.
 */
import { type FormEvent, type ReactElement, useEffect, useId, useState } from 'react';
import type { GeoUsageView, GeoVendorSlotView } from '@vello/api-client';
import { formatMoney } from '@vello/format';
import { Badge, Button, Field, Notice, Panel, Select, Table, TextInput, FormGrid, Row, Strong, Text, Stack, FormStack, Divided } from '@vello/web-theme';
import {
  GEO_SLOT_ISSUE_MESSAGE,
  failurePercent,
  geoSlotFormFromView,
  getGeoProviders,
  getGeoUsage,
  normalizeGeoSlotForm,
  probeGeoRouter,
  routerHealthLines,
  setGeoProvider,
  usageByProvider,
  type GeoSlotField,
  type GeoSlotForm,
} from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { ScopeMarketNotice, useScopeMarket } from './scope-market';

/** One vendor's form. */
function VendorSlot({ view, onSaved }: { view: GeoVendorSlotView; onSaved: (next: GeoVendorSlotView) => void }): ReactElement {
  const { runtime } = useAdmin();
  const { t } = useT();
  const { fail, v, explain } = useVocab();
  const id = useId();
  const [form, setForm] = useState<GeoSlotForm>(() => geoSlotFormFromView(view));
  const [errors, setErrors] = useState<Partial<Record<GeoSlotField, string>>>({});
  const [msg, setMsg] = useState<{ tone: 'success' | 'danger'; text: string } | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof GeoSlotForm>(field: K) => (v: GeoSlotForm[K]): void => setForm((f) => ({ ...f, [field]: v }));

  const onSave = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setMsg(undefined);
    const normalized = normalizeGeoSlotForm(view, form);
    if (!normalized.ok) {
      const next: Partial<Record<GeoSlotField, string>> = {};
      for (const issue of normalized.error) next[issue.field] = t(GEO_SLOT_ISSUE_MESSAGE[issue.code]);
      setErrors(next);
      return;
    }
    setErrors({});
    setBusy(true);
    const res = await setGeoProvider(runtime, view.vendor, normalized.value);
    setBusy(false);
    if (res.ok) {
      onSaved(res.value);
      setForm(geoSlotFormFromView(res.value));
      setMsg({ tone: 'success', text: t('geoint.saved') });
    } else {
      setMsg({ tone: 'danger', text: explain(res.error) ?? t('geoint.save.failed', { code: fail(res.error) }) });
    }
  };

  return (
    <FormStack divided onSubmit={(e) => void onSave(e)} noValidate aria-labelledby={`${id}-h`}>
      <Row gap={3} align="baseline" wrap>
        <Strong as="h3" role="body" id={`${id}-h`}>{v('geo.vendor', view.vendor)}</Strong>
        {view.requires === 'apiKey' ? (
          <Badge tone={view.configured ? 'success' : 'warning'}>{view.configured ? t('int.status.configured') : t('int.status.notConfigured')}</Badge>
        ) : (
          // D854 (L7a-011): a self-hosted service is reached by its address; it has no key, so it never claims "Chave configurada".
          <Badge tone={view.configured ? 'neutral' : 'warning'}>{view.configured ? t('int.status.noKeyNeeded') : t('int.status.noAddress')}</Badge>
        )}
        {view.enabled && <Badge tone="success">{t('int.status.enabled')}</Badge>}
      </Row>
      <FormGrid>
        <Field label={t('geoint.field.enabled')} htmlFor={`${id}-enabled`}>
          <Select id={`${id}-enabled`} value={form.enabled ? 'on' : 'off'} onChange={(e) => set('enabled')(e.target.value === 'on')}>
            <option value="on">{t('geoint.field.enabled.on')}</option>
            <option value="off">{t('geoint.field.enabled.off')}</option>
          </Select>
        </Field>
        {view.requires === 'apiKey' ? (
          <Field label={t('geoint.field.apiKey')} htmlFor={`${id}-key`} hint={errors.apiKey ?? (view.configured ? t('geoint.field.apiKey.hintConfigured', { hint: view.maskedHint ?? '' }) : t('geoint.field.apiKey.hint'))}>
            <TextInput id={`${id}-key`} type="password" autoComplete="off" value={form.apiKey} onChange={(e) => set('apiKey')(e.target.value)} aria-invalid={errors.apiKey !== undefined} />
          </Field>
        ) : (
          <Field label={t('geoint.field.baseUrl')} htmlFor={`${id}-url`} hint={errors.baseUrl ?? t('geoint.field.baseUrl.hint')}>
            <TextInput id={`${id}-url`} type="url" inputMode="url" autoComplete="off" value={form.baseUrl} onChange={(e) => set('baseUrl')(e.target.value)} aria-invalid={errors.baseUrl !== undefined} />
          </Field>
        )}
        {view.vendor === 'osrm' && (
          <Field label={t('geoint.field.motorcycleBaseUrl')} htmlFor={`${id}-moto-url`} hint={errors.motorcycleBaseUrl ?? t('geoint.field.motorcycleBaseUrl.hint')}>
            <TextInput id={`${id}-moto-url`} type="url" inputMode="url" autoComplete="off" value={form.motorcycleBaseUrl} onChange={(e) => set('motorcycleBaseUrl')(e.target.value)} aria-invalid={errors.motorcycleBaseUrl !== undefined} />
          </Field>
        )}
        {view.vendor === 'nominatim' && (
          <Field label={t('geoint.field.userAgent')} htmlFor={`${id}-ua`} hint={errors.userAgent}>
            <TextInput id={`${id}-ua`} autoComplete="off" value={form.userAgent} onChange={(e) => set('userAgent')(e.target.value)} />
          </Field>
        )}
        <Field label={t('geoint.field.cost')} htmlFor={`${id}-cost`} hint={errors.cost ?? t('geoint.field.cost.hint')}>
          <Row gap={2} align="stretch">
            <TextInput id={`${id}-cost`} className="tnum" inputMode="numeric" value={form.costPerRequestMinor} onChange={(e) => set('costPerRequestMinor')(e.target.value)} aria-invalid={errors.cost !== undefined} />
            <TextInput id={`${id}-currency`} aria-label={t('geoint.field.currency')} placeholder={t('geoint.field.currency.placeholder')} value={form.costCurrency} maxLength={3} onChange={(e) => set('costCurrency')(e.target.value)} className="vello-admin-a-narrow" />
          </Row>
        </Field>
      </FormGrid>
      {msg !== undefined && (
        <div>
          <Notice tone={msg.tone}>{msg.text}</Notice>
        </div>
      )}
      <div>
        <Button type="submit" variant="secondary" disabled={busy}>{busy ? t('geoint.saving') : t('geoint.save')}</Button>
      </div>
    </FormStack>
  );
}

/** The self-hosted router probe at a market's centre. */
function RouterProbe(): ReactElement {
  const { runtime } = useAdmin();
  const { t } = useT();
  const { fail } = useVocab();
  // The probe runs at the header scope's market (owner, 2026-10-12) — no second market field.
  const { market, choices } = useScopeMarket();
  const [busy, setBusy] = useState(false);
  const [msgs, setMsgs] = useState<readonly { tone: 'success' | 'danger' | 'warning'; text: string }[]>([]);
  const run = async (): Promise<void> => {
    if (market === undefined) return;
    setBusy(true);
    setMsgs([]);
    const res = await probeGeoRouter(runtime, { marketId: market });
    setBusy(false);
    if (!res.ok) {
      setMsgs([{ tone: 'danger', text: t('geoint.health.failed', { code: fail(res.error) }) }]);
      return;
    }
    // D847: with a motorcycle router registered the car graph and the motorcycle router each show their own status; alone, the car line as before.
    const lines = routerHealthLines(res.value);
    const say = (label: string, state: NonNullable<typeof lines.motorcycle>): { tone: 'success' | 'danger' | 'warning'; text: string } => ({ tone: state.tone, text: `${label}: ${t(state.messageId, { detail: state.detail ?? '' })}` });
    setMsgs(
      lines.motorcycle === undefined
        ? [{ tone: lines.car.tone, text: t(lines.car.messageId, { detail: lines.car.detail ?? '' }) }]
        : [say(t('geoint.health.label.car'), lines.car), say(t('geoint.health.label.motorcycle'), lines.motorcycle)],
    );
  };
  return (
    <Divided>
      <Strong as="h3" role="body">{t('geoint.health.heading')}</Strong>
      {market === undefined ? (
        <ScopeMarketNotice choices={choices} />
      ) : (
        <div>
          <Button variant="secondary" disabled={busy} onClick={() => void run()}>{busy ? t('geoint.health.running') : t('geoint.health.run')}</Button>
        </div>
      )}
      {msgs.map((m) => (
        <Notice key={m.text} tone={m.tone}>{m.text}</Notice>
      ))}
    </Divided>
  );
}

/** Usage and cost of the last 30 days, per provider. */
function UsageTable({ usage }: { usage: GeoUsageView }): ReactElement {
  const { t, lang } = useT();
  const { v } = useVocab();
  const rows = usageByProvider(usage);
  return (
    <Divided>
      <Strong as="h3" role="body">{t('geoint.usage.heading')}</Strong>
      <Table
        rows={rows}
        rowKey={(r) => r.providerId}
        empty={t('geoint.usage.empty')}
        columns={[
          { key: 'provider', header: t('geoint.usage.provider'), render: (r) => v('ride.route_provider', r.providerId) },
          { key: 'calls', header: t('geoint.usage.calls'), numeric: true, render: (r) => <span className="tnum">{r.calls}</span> },
          { key: 'cached', header: t('geoint.usage.cached'), numeric: true, render: (r) => <span className="tnum">{r.cachedHits}</span> },
          {
            key: 'failures',
            header: t('geoint.usage.failures'),
            numeric: true,
            render: (r) => {
              const pct = failurePercent(r);
              return <span className="tnum">{pct !== undefined && r.failures > 0 ? t('geoint.usage.failures.rate', { count: r.failures, percent: pct }) : r.failures}</span>;
            },
          },
          {
            // D854 (L7a-013): why the calls failed, in the words of the stable reason (most frequent first), never only a counter.
            key: 'reasons',
            header: t('geoint.usage.reasons'),
            render: (r) =>
              r.failureReasons.length === 0 ? (
                <Text role="caption" tone="secondary">—</Text>
              ) : (
                <Stack gap={1}>
                  {r.failureReasons.map((f) => (
                    <Text key={f.reason} role="caption"><span className="tnum">{f.count}</span> · {v('geo.route_reason', f.reason)}</Text>
                  ))}
                </Stack>
              ),
          },
          {
            key: 'cost',
            header: t('geoint.usage.cost'),
            numeric: true,
            render: (r) => (
              <span className="tnum">
                {r.costs.length === 0 ? t('geoint.usage.free') : r.costs.map((c) => formatMoney({ amountMinor: c.amountMinor, currency: c.currency }, { locale: lang, timeZone: 'UTC', measurement: 'metric' })).join(' · ')}
              </span>
            ),
          },
        ]}
      />
    </Divided>
  );
}

/** The whole panel: every vendor slot, the router probe and the usage. */
export function GeoProvidersPanel(): ReactElement {
  const { runtime } = useAdmin();
  const { t } = useT();
  const { fail } = useVocab();
  const [slots, setSlots] = useState<readonly GeoVendorSlotView[] | undefined>(undefined);
  const [usage, setUsage] = useState<GeoUsageView | undefined>(undefined);
  const [loadMsg, setLoadMsg] = useState<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [res, used] = await Promise.all([getGeoProviders(runtime), getGeoUsage(runtime)]);
      if (cancelled) return;
      if (res.ok) setSlots(res.value.providers);
      else setLoadMsg(t('geoint.load.failed', { code: fail(res.error) }));
      if (used.ok) setUsage(used.value);
    })();
    return () => {
      cancelled = true;
    };
  }, [runtime, t]);

  const replace = (next: GeoVendorSlotView): void => setSlots((cur) => (cur ?? []).map((s) => (s.vendor === next.vendor ? next : s)));

  return (
    <Panel><Stack gap={4}>
      <Text as="h2" role="title">{t('geoint.heading')}</Text>
      <Text as="p" role="body" tone="secondary">{t('geoint.hint')}</Text>
      {loadMsg !== undefined && <Notice tone="danger">{loadMsg}</Notice>}
      {(slots ?? []).map((view) => (
        <VendorSlot key={view.vendor} view={view} onSaved={replace} />
      ))}
      {slots !== undefined && <RouterProbe />}
      {usage !== undefined && <UsageTable usage={usage} />}
    </Stack></Panel>
  );
}
